export const LIMITES = {
  nombreMin: 2,
  nombreMax: 120,
  emailMax: 120,
  telefonoMin: 6,
  telefonoMax: 20,
  direccionMax: 200,
  ciudadMax: 80,
  cpMax: 10,
  notasMax: 2000,
  nifMax: 12,
};

const NOMBRE_RE = /^[\p{L}\p{M}0-9 .,'\-&·"·º\u00BA\u00AAºª/()]+$/u;
const CIUDAD_RE = /^[\p{L}\p{M}][\p{L}\p{M} '.\-]{0,79}$/u;
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const TELEFONO_RE = /^[+0-9 ()\-./]+$/;
const TELEFONO_DIGITS_RE = /^[0-9]{6,15}$/;
const CP_ES_RE = /^[0-9]{5}$/;

const DNI_RE = /^[0-9]{8}[A-Z]$/;
const NIE_RE = /^[XYZ][0-9]{7}[A-Z]$/;
const CIF_RE = /^[ABCDEFGHJKLMNPQRSUVW][0-9]{7}[0-9A-J]$/;
const DNI_LETRAS = "TRWAGMYFPDXBNJZSQVHLCKE";

function letraDNI(num) {
  return DNI_LETRAS[num % 23];
}

function validarDniEs(value) {
  const v = value.toUpperCase();
  if (DNI_RE.test(v)) {
    return letraDNI(parseInt(v.slice(0, 8), 10)) === v[8];
  }
  if (NIE_RE.test(v)) {
    const map = { X: "0", Y: "1", Z: "2" };
    const num = parseInt(map[v[0]] + v.slice(1, 8), 10);
    return letraDNI(num) === v[8];
  }
  return false;
}

function validarCifEs(value) {
  const v = value.toUpperCase();
  if (!CIF_RE.test(v)) return false;
  const digits = v.slice(1, 8);
  let parImpar = 0;
  for (let i = 0; i < digits.length; i++) {
    const n = parseInt(digits[i], 10);
    if (i % 2 === 0) {
      const d = n * 2;
      parImpar += Math.floor(d / 10) + (d % 10);
    } else {
      parImpar += n;
    }
  }
  const ctrl = (10 - (parImpar % 10)) % 10;
  const last = v[8];
  const lettersCtrl = "JABCDEFGHI";
  const orgsLetra = "PQRSNW";
  if (orgsLetra.includes(v[0])) return last === lettersCtrl[ctrl];
  if ("ABEH".includes(v[0])) return last === String(ctrl);
  return last === String(ctrl) || last === lettersCtrl[ctrl];
}

export function validarNif(value) {
  if (!value) return null;
  const v = String(value).trim().toUpperCase().replace(/[\s-]/g, "");
  if (v.length > LIMITES.nifMax) return "demasiado largo";
  if (validarDniEs(v) || validarCifEs(v)) return null;
  return "NIF/CIF/NIE no válido";
}

export function validarEmail(value, { obligatorio = false } = {}) {
  if (!value) return obligatorio ? "obligatorio" : null;
  const v = String(value).trim();
  if (v.length > LIMITES.emailMax) return "demasiado largo";
  if (!EMAIL_RE.test(v)) return "email no válido";
  return null;
}

export function validarTelefono(value, { obligatorio = false } = {}) {
  if (!value) return obligatorio ? "obligatorio" : null;
  const v = String(value).trim();
  if (v.length < LIMITES.telefonoMin || v.length > LIMITES.telefonoMax) {
    return `entre ${LIMITES.telefonoMin} y ${LIMITES.telefonoMax} caracteres`;
  }
  if (!TELEFONO_RE.test(v)) return "solo dígitos, espacios, +, -, ()";
  const digits = v.replace(/[^0-9]/g, "");
  if (!TELEFONO_DIGITS_RE.test(digits)) {
    return "número incompleto";
  }
  return null;
}

export function validarNombre(value, { obligatorio = true } = {}) {
  if (!value || !String(value).trim()) {
    return obligatorio ? "obligatorio" : null;
  }
  const v = String(value).trim();
  if (v.length < LIMITES.nombreMin) return `mínimo ${LIMITES.nombreMin} caracteres`;
  if (v.length > LIMITES.nombreMax) return `máximo ${LIMITES.nombreMax} caracteres`;
  if (!NOMBRE_RE.test(v)) return "caracteres no permitidos";
  return null;
}

export function validarCiudad(value) {
  if (!value) return null;
  const v = String(value).trim();
  if (v.length > LIMITES.ciudadMax) return `máximo ${LIMITES.ciudadMax} caracteres`;
  if (!CIUDAD_RE.test(v)) return "solo letras, espacios y guiones";
  return null;
}

export function validarCp(value, { pais = "ES" } = {}) {
  if (!value) return null;
  const v = String(value).trim();
  if (v.length > LIMITES.cpMax) return `máximo ${LIMITES.cpMax} caracteres`;
  if (pais === "ES" && !CP_ES_RE.test(v)) return "código postal de 5 dígitos";
  return null;
}

export function validarDireccion(value) {
  if (!value) return null;
  const v = String(value).trim();
  if (v.length > LIMITES.direccionMax) {
    return `máximo ${LIMITES.direccionMax} caracteres`;
  }
  return null;
}

export function validarNotas(value) {
  if (!value) return null;
  const v = String(value);
  if (v.length > LIMITES.notasMax) {
    return `máximo ${LIMITES.notasMax} caracteres`;
  }
  return null;
}

export function validarClienteForm(form) {
  const errores = {};
  const nombreErr = validarNombre(form.nombre);
  if (nombreErr) errores.nombre = nombreErr;
  const nifErr = validarNif(form.nif);
  if (nifErr) errores.nif = nifErr;
  const emailErr = validarEmail(form.email);
  if (emailErr) errores.email = emailErr;
  const telErr = validarTelefono(form.telefono);
  if (telErr) errores.telefono = telErr;
  const direccionErr = validarDireccion(form.direccion);
  if (direccionErr) errores.direccion = direccionErr;
  const ciudadErr = validarCiudad(form.ciudad);
  if (ciudadErr) errores.ciudad = ciudadErr;
  const cpErr = validarCp(form.cp);
  if (cpErr) errores.cp = cpErr;
  const notasErr = validarNotas(form.notas);
  if (notasErr) errores.notas = notasErr;
  return errores;
}

export function sanearTelefono(value) {
  if (!value) return "";
  return String(value).replace(/[^0-9+\s\-().]/g, "").slice(0, LIMITES.telefonoMax);
}

export function sanearCp(value) {
  if (!value) return "";
  return String(value).replace(/[^0-9]/g, "").slice(0, LIMITES.cpMax);
}

export function sanearNif(value) {
  if (!value) return "";
  return String(value).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, LIMITES.nifMax);
}

export function sanearCiudad(value) {
  if (!value) return "";
  return String(value)
    .replace(/[^\p{L}\p{M} '.\-]/gu, "")
    .slice(0, LIMITES.ciudadMax);
}

export function sanearNombre(value) {
  if (!value) return "";
  return String(value).slice(0, LIMITES.nombreMax);
}
